import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MessageCircle, Lock } from 'lucide-react';
import { CONTACT, NAV_LINKS, ASSETS } from '@/data/content';
import { scrollToHash } from '@/lib/motion';

const DESKTOP_LINKS = NAV_LINKS.filter(l => ['#why', '#programs', '#age-groups', '#coaches', '#gallery', '#contact'].includes(l.hash));

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToHash(hash), 150);
    } else {
      scrollToHash(hash);
    }
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled || open ? 'bg-black/85 backdrop-blur-xl border-b border-white/10' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <button onClick={() => go('#home')} className="flex items-center gap-3 group" data-testid="navbar-logo">
            <img src={ASSETS.logo} alt="ASA crest" className="h-10 w-10 sm:h-11 sm:w-11 rounded-full object-cover ring-1 ring-brand-gold/60 group-hover:ring-brand-gold transition-[box-shadow] duration-300" />
            <span className="text-left leading-none">
              <span className="block font-display text-xl sm:text-2xl tracking-wide text-white">ABUZAMA</span>
              <span className="block text-[9px] sm:text-[10px] tracking-[0.32em] text-brand-gold font-semibold">SOCCER ACADEMY</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7" data-testid="navbar-desktop-links">
            {DESKTOP_LINKS.map(l => (
              <button key={l.hash} onClick={() => go(l.hash)} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-sm font-semibold tracking-wide text-gray-300 hover:text-brand-gold transition-colors duration-300 uppercase">
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/register" data-testid="nav-register-button"
              className="whitespace-nowrap bg-brand-red hover:bg-brand-redhover text-white font-display tracking-widest text-base sm:text-lg px-5 sm:px-7 py-2 sm:py-2.5 rounded-sm transition-colors duration-300">
              REGISTER NOW
            </Link>
            <button onClick={() => setOpen(!open)} data-testid="nav-mobile-menu-button"
              className="lg:hidden text-white p-2" aria-label="Toggle menu">
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-night/97 backdrop-blur-2xl lg:hidden pt-24 px-6 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1 pb-10">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.hash}
                  initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, duration: 0.4 }}
                  onClick={() => go(l.hash)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-left font-display text-4xl text-white py-3 border-b border-white/5 hover:text-brand-gold transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex flex-col gap-3 mt-8">
                <Link to="/register" onClick={() => setOpen(false)} data-testid="mobile-menu-register-button"
                  className="bg-brand-red text-white text-center font-display tracking-widest text-2xl py-4 rounded-sm">
                  REGISTER NOW
                </Link>
                <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="mobile-menu-whatsapp-button"
                  className="border border-[#25D366]/60 text-[#25D366] text-center font-display tracking-widest text-2xl py-4 rounded-sm flex items-center justify-center gap-2">
                  <MessageCircle size={22} /> WHATSAPP US
                </a>
                <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-menu-admin-login"
                  className="border border-brand-gold/60 text-brand-gold text-center font-display tracking-widest text-2xl py-4 rounded-sm flex items-center justify-center gap-2">
                  <Lock size={22} /> ADMIN LOGIN — دخول المدير
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
